import React, { useEffect, useMemo, useState } from 'react';
import { Button, FormInput, FormNumberInput, Modal } from '@/components/UI';
import { showToast } from '@/components/Layout/Toast';
import { trpc } from '@/api/trpc';
import { useTrpcMutation } from '@/api/useTrpcMutation';
import { DEFAULT_REQUISITIES_STALE_TIME } from '@/config/constants';
import { useAppQuery } from '@/hooks/useAppQuery';
import { FaFilter, FaSave, FaSpinner, FaTimes } from 'react-icons/fa';

interface CreateUniverseScreenerModalProps {
    isOpen: boolean;
    onClose: () => void;
    experimentId: number;
    experimentName: string;
    duration?: string;
    onSuccess?: (createdScreenerRuleIds: number[]) => void;
}

interface FilterCriteria {
    minAlphaPercent?: number;
    minProfitPercent?: number;
    minWinRate?: number;
    minWinRateVsSpy?: number;
    maxAvgLossPercent?: number;
    minTotalEntries?: number;
    minOptionProfitPercent?: number;
    minOptionWinRate?: number;
    minMedianProfitPercent?: number;
    minMedianAlphaPercent?: number;
    minMedianOptionProfitPercent?: number;
    minMedianOptionMaxProfitPercent?: number;
    maxCoefficientOfVariation?: number;
    minProfitFactor?: number;
}

const formatTimeHorizon = (seconds: number): string => {
    const minute = 60;
    const hour = 3600;
    const day = 86400;
    const week = 604800;
    const month = 2592000;
    const year = 31536000;

    if (seconds < hour) return `${Math.floor(seconds / minute)}m`;
    if (seconds < day) return `${Math.floor(seconds / hour)}h`;
    if (seconds < week) return `${Math.floor(seconds / day)}d`;
    if (seconds < month) return `${Math.floor(seconds / week)}w`;
    if (seconds < year) return `${Math.floor(seconds / month)}mo`;
    return `${Math.floor(seconds / year)}y`;
};

const CreateUniverseScreenerModal: React.FC<CreateUniverseScreenerModalProps> = ({
    isOpen,
    onClose,
    experimentId,
    experimentName,
    duration,
    onSuccess
}) => {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [filters, setFilters] = useState<FilterCriteria>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [selectedDurations, setSelectedDurations] = useState<string[]>([]);
    const [selectedHorizons, setSelectedHorizons] = useState<number[]>([]);
    const [maxEntitiesPerType, setMaxEntitiesPerType] = useState<number | null>(500);
    const [isActive, setIsActive] = useState<boolean>(true);

    const [previewData, setPreviewData] = useState<{
        totalFiltered: number;
        totalOriginal: number;
        filteredEntities: {
            entityId: number;
            entityType: string;
            symbol: string;
            metrics: {
                avgProfitPercent: number | null;
                avgAlphaPercent: number | null;
                winRate: number | null;
                winRateVsSpy: number | null;
                totalEntries: number;
            };
        }[];
    } | null>(null);

    const mutation = useTrpcMutation(trpc.experiment.createUniverseScreenerFromResults.mutationOptions({}), {
        successMessage: undefined,
        fallbackErrorMessage: 'Failed to create universe screener'
    });

    const durationsQuery = useAppQuery({
        ...trpc.experiment.getDurationsAndHorizons.queryOptions({ experimentId }),
        enabled: isOpen,
        staleTime: DEFAULT_REQUISITIES_STALE_TIME
    });

    useEffect(() => {
        if (!isOpen) return;

        setName(`Universe Screener - ${experimentName}`);
        setDescription(`Universe screener derived from experiment "${experimentName}" (symbol allow-list MVP).`);
        setFilters({});
        setPreviewData(null);
        setMaxEntitiesPerType(500);
        setIsActive(true);

        if (durationsQuery.data) {
            if (duration) {
                setSelectedDurations([duration]);
            } else {
                setSelectedDurations(durationsQuery.data.durations);
            }

            setSelectedHorizons(durationsQuery.data.horizons);
        }
    }, [isOpen, experimentName, duration, durationsQuery.data]);

    const availableHorizons = useMemo(() => {
        if (!durationsQuery.data || selectedDurations.length === 0) return [];

        const horizonSet = new Set<number>();

        selectedDurations.forEach((dur) => {
            const horizons = durationsQuery.data.durationHorizonMap[dur] || [];
            horizons.forEach((h: number) => horizonSet.add(h));
        });

        return Array.from(horizonSet).sort((a, b) => a - b);
    }, [selectedDurations, durationsQuery.data]);

    const handleFilterChange = (field: keyof FilterCriteria, value: string) => {
        const numValue = value === '' ? undefined : parseFloat(value);
        setFilters((prev) => ({ ...prev, [field]: numValue }));
        setPreviewData(null);
    };

    const handlePreview = async () => {
        try {
            setIsSubmitting(true);

            const result = await mutation.mutateAsync({
                experimentId,
                name: 'preview',
                filters,
                durations: selectedDurations.length > 0 ? selectedDurations : duration ? [duration] : undefined,
                horizons: selectedHorizons.length > 0 ? selectedHorizons : undefined,
                maxEntitiesPerType: maxEntitiesPerType ?? undefined,
                isActive,
                previewOnly: true
            });

            if (!result.success) {
                showToast(result.errorMessages?.[0] || 'Failed to preview filtered entities', 'error');
                return;
            }

            setPreviewData({
                filteredEntities: result.filteredEntities,
                totalFiltered: result.totalFiltered,
                totalOriginal: result.totalOriginal
            });
        } catch (error) {
            showToast('Failed to preview filtered entities', 'error');

            console.error('Preview error:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCreate = async () => {
        if (!name.trim()) {
            showToast('Please enter a name for the screener', 'error');
            return;
        }

        if (maxEntitiesPerType === null || maxEntitiesPerType <= 0) {
            showToast('Max entities per type must be a positive number', 'error');
            return;
        }

        try {
            setIsSubmitting(true);

            const result = await mutation.mutateAsync({
                experimentId,
                name,
                description: description || undefined,
                filters,
                durations: selectedDurations.length > 0 ? selectedDurations : duration ? [duration] : undefined,
                horizons: selectedHorizons.length > 0 ? selectedHorizons : undefined,
                maxEntitiesPerType,
                isActive
            });

            if (!result.success) {
                showToast(result.errorMessages?.[0] || 'Failed to create universe screener', 'error');
                return;
            }

            const screenerIds = result.createdScreeners?.map((s) => s.screenerRuleId) ?? [];

            if (screenerIds.length > 0) {
                showToast(`Created ${screenerIds.length} screener rule(s)`, 'success');
                onSuccess?.(screenerIds);
            } else {
                showToast('No screener rules were created (no matching entities)', 'warning');
            }

            onClose();
        } catch (error) {
            showToast('Failed to create universe screener', 'error');

            console.error('Create error:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const selectedHorizonLabels = useMemo(() => {
        if (!durationsQuery.data) return '';

        const selected = selectedHorizons
            .slice()
            .sort((a, b) => a - b)
            .map(formatTimeHorizon);

        return selected.join(', ');
    }, [selectedHorizons, durationsQuery.data]);

    const canSubmit = !isSubmitting && !mutation.isPending;

    return (
        <Modal isOpen={isOpen} size="xl" onClose={onClose}>
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-white">Create Universe Screener</h2>
                <button className="text-zinc-400 hover:text-white" onClick={onClose}>
                    <FaTimes />
                </button>
            </div>

            <div className="text-zinc-400 text-sm mb-6">
                Creates one screener per entity type (Asset, Crypto) using a symbol allow-list derived from filtered experiment results.
            </div>

            <div className="space-y-4">
                <FormInput
                    label="Screener Name"
                    placeholder="Universe screener name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                />

                <FormInput
                    label="Description"
                    placeholder="Optional description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                />

                <div className="grid grid-cols-2 gap-4">
                    <FormNumberInput
                        integer
                        label="Max Entities Per Type"
                        min={1}
                        placeholder="500"
                        value={maxEntitiesPerType}
                        onValueChange={setMaxEntitiesPerType}
                    />
                    <div className="flex items-center gap-3 mt-7">
                        <input
                            checked={isActive}
                            className="h-4 w-4"
                            id="isActive"
                            type="checkbox"
                            onChange={(e) => setIsActive(e.target.checked)}
                        />
                        <label className="text-white" htmlFor="isActive">
                            Create as active
                        </label>
                    </div>
                </div>

                {durationsQuery.data && (
                    <div className="bg-zinc-950 border border-zinc-800 rounded p-4">
                        <div className="text-white font-semibold mb-2">Durations</div>
                        <div className="flex flex-wrap gap-2">
                            {durationsQuery.data.durations.map((dur) => {
                                const isSelected = selectedDurations.includes(dur);
                                return (
                                    <Button
                                        key={dur}
                                        size="sm"
                                        variant={isSelected ? 'primary' : 'secondary'}
                                        onClick={() => {
                                            setSelectedDurations((prev) =>
                                                prev.includes(dur) ? prev.filter((d) => d !== dur) : [...prev, dur]
                                            );

                                            setPreviewData(null);
                                        }}
                                    >
                                        {dur}
                                    </Button>
                                );
                            })}
                        </div>

                        <div className="text-white font-semibold mt-4 mb-2">Horizons</div>
                        <div className="text-zinc-500 text-sm mb-2">
                            Selected: {selectedHorizons.length} ({selectedHorizonLabels || 'none'})
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {availableHorizons.map((h) => {
                                const isSelected = selectedHorizons.includes(h);
                                return (
                                    <Button
                                        key={h}
                                        size="sm"
                                        variant={isSelected ? 'primary' : 'secondary'}
                                        onClick={() => {
                                            setSelectedHorizons((prev) => (prev.includes(h) ? prev.filter((x) => x !== h) : [...prev, h]));
                                            setPreviewData(null);
                                        }}
                                    >
                                        {formatTimeHorizon(h)}
                                    </Button>
                                );
                            })}
                        </div>
                    </div>
                )}

                <div className="bg-zinc-950 border border-zinc-800 rounded p-4">
                    <div className="flex items-center gap-2 text-white font-semibold mb-3">
                        <FaFilter />
                        Filters
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <FormInput
                            label="Min Avg Profit %"
                            placeholder="e.g. 0.5"
                            value={filters.minProfitPercent?.toString() || ''}
                            onChange={(e) => handleFilterChange('minProfitPercent', e.target.value)}
                        />
                        <FormInput
                            label="Min Win Rate"
                            placeholder="e.g. 0.55"
                            value={filters.minWinRate?.toString() || ''}
                            onChange={(e) => handleFilterChange('minWinRate', e.target.value)}
                        />
                        <FormInput
                            label="Min Alpha %"
                            placeholder="e.g. 0.25"
                            value={filters.minAlphaPercent?.toString() || ''}
                            onChange={(e) => handleFilterChange('minAlphaPercent', e.target.value)}
                        />
                        <FormInput
                            label="Min Total Entries"
                            placeholder="e.g. 50"
                            value={filters.minTotalEntries?.toString() || ''}
                            onChange={(e) => handleFilterChange('minTotalEntries', e.target.value)}
                        />
                        <FormInput
                            label="Max Avg Loss %"
                            placeholder="e.g. -2"
                            value={filters.maxAvgLossPercent?.toString() || ''}
                            onChange={(e) => handleFilterChange('maxAvgLossPercent', e.target.value)}
                        />
                        <FormInput
                            label="Min Profit Factor"
                            placeholder="e.g. 1.5"
                            value={filters.minProfitFactor?.toString() || ''}
                            onChange={(e) => handleFilterChange('minProfitFactor', e.target.value)}
                        />
                    </div>
                </div>

                {previewData && (
                    <div className="bg-zinc-950 border border-zinc-800 rounded p-4">
                        <div className="text-white font-semibold mb-2">Preview</div>
                        <div className="text-zinc-400 text-sm">
                            Filtered {previewData.totalFiltered} of {previewData.totalOriginal} entities.
                        </div>
                    </div>
                )}

                <div className="flex items-center justify-end gap-2 pt-2">
                    <Button disabled={!canSubmit} variant="secondary" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button disabled={!canSubmit} variant="secondary" onClick={handlePreview}>
                        {isSubmitting ? <FaSpinner className="animate-spin inline-block mr-2" /> : null}
                        Preview
                    </Button>
                    <Button disabled={!canSubmit} variant="primary" onClick={handleCreate}>
                        {isSubmitting ? <FaSpinner className="animate-spin inline-block mr-2" /> : <FaSave className="inline-block mr-2" />}
                        Create Screener
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default CreateUniverseScreenerModal;
